import { useMemo, useState } from 'react';

/**
 * Der Nachrichtentext, lesbar dargestellt.
 *
 * Zwei Dinge, die vorher fehlten:
 *
 * 1. Zitierte Zeilen (`>`) werden eingeklappt. In einer Unterhaltung mit fünf
 *    Antworten stand sonst fünfmal derselbe alte Text untereinander, und das
 *    Neue ging darin unter.
 * 2. Links werden anklickbar. Der Server liefert reinen Text — HTML aus fremden
 *    Mails wird bewusst nicht in die Seite gelassen —, aber eine Adresse im
 *    Text soll man trotzdem anklicken können.
 */
export function MessageBody({ text }: { text: string | null }) {
  const [zeigeZitat, setZeigeZitat] = useState(false);

  const { neu, zitat } = useMemo(() => teileZitat(text ?? ''), [text]);

  if (!text?.trim()) {
    return (
      <p className="mt-2 text-sm italic text-muted-foreground">
        Kein Text übertragen — die Nachricht enthält vermutlich nur Anhänge.
      </p>
    );
  }

  return (
    <div className="mt-2 text-sm">
      <p className="whitespace-pre-wrap break-words">{mitLinks(neu)}</p>

      {zitat && (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => setZeigeZitat((v) => !v)}
            className="rounded border px-2 py-0.5 text-xs text-muted-foreground hover:bg-muted"
            aria-expanded={zeigeZitat}
          >
            {zeigeZitat ? 'Zitierten Text ausblenden' : '··· Zitierten Text anzeigen'}
          </button>
          {zeigeZitat && (
            <p className="mt-2 whitespace-pre-wrap break-words border-l-2 pl-3 text-muted-foreground">
              {zitat}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Trennt den neuen Teil vom zitierten.
 *
 * Erkannt wird beides, was in der Praxis vorkommt: Zeilen mit `>` und die
 * Einleitungszeile, die Mailprogramme darüber setzen („Am 01.05.2026 schrieb
 * …"). Ab der ersten solchen Zeile gilt der Rest als Zitat.
 */
function teileZitat(text: string): { neu: string; zitat: string | null } {
  const zeilen = text.split('\n');

  const einleitung =
    /^(am\s.+\sschrieb\s|on\s.+\swrote:|-{2,}\s*(urspr[üu]ngliche|original)\s)/i;

  let grenze = -1;
  for (let i = 0; i < zeilen.length; i++) {
    const zeile = (zeilen[i] ?? '').trim();
    if (zeile.startsWith('>') || einleitung.test(zeile)) {
      grenze = i;
      break;
    }
  }

  if (grenze < 0) return { neu: text.trim(), zitat: null };

  const neu = zeilen.slice(0, grenze).join('\n').trim();
  const zitat = zeilen.slice(grenze).join('\n').trim();

  // Besteht die Nachricht nur aus Zitat, gibt es nichts einzuklappen —
  // dann lieber alles zeigen als eine leere Nachricht.
  if (!neu) return { neu: zitat, zitat: null };

  return { neu, zitat: zitat || null };
}

const URL_MUSTER = /(https?:\/\/[^\s<>()]+|www\.[^\s<>()]+|[\w.+-]+@[\w-]+\.[\w.-]+)/g;

/** Macht Adressen im Text anklickbar, ohne fremdes HTML einzulassen. */
function mitLinks(text: string): React.ReactNode[] {
  const teile = text.split(URL_MUSTER);

  return teile.map((teil, i) => {
    if (!URL_MUSTER.test(teil)) {
      URL_MUSTER.lastIndex = 0;
      return teil;
    }
    URL_MUSTER.lastIndex = 0;

    const istMail = teil.includes('@') && !teil.startsWith('http');
    const ziel = istMail
      ? `mailto:${teil}`
      : teil.startsWith('http')
        ? teil
        : `https://${teil}`;

    return (
      <a
        key={i}
        href={ziel}
        target={istMail ? undefined : '_blank'}
        // noreferrer verhindert, dass die Zielseite erfährt, woher der Klick
        // kam, und schließt den window.opener-Zugriff aus.
        rel="noopener noreferrer"
        className="text-primary underline underline-offset-2"
      >
        {teil}
      </a>
    );
  });
}
